// Keyless, offline "rough" flight-search engine.
//
// There is no free, key-less API that returns real airfares (every provider —
// Amadeus, Duffel, Aviationstack, Skyscanner — gates prices behind a paid key).
// So this module *estimates* schedules and fares deterministically from real
// airport geography: distance sets the base fare and flight duration, and a
// seeded hash of (route + date) adds stable day-to-day variation, plus the
// usual real-world curves (booking window, day-of-week, season, time-of-day).
//
// Everything is a rough guide, NOT a bookable quote. When you later get an API
// key, replace the body of `searchFlights` / `priceStrip` with real fetches —
// the return shapes are the whole contract the UI depends on.

import { AIRPORTS } from "../data/airports.js";
import { haversineKm } from "./geo.js";

// ── Airport helpers ──────────────────────────────────────────────────────────
export const SEARCH_AIRPORTS = AIRPORTS.map((a) => ({
  icao: a.icao,
  iata: a.iata,
  name: a.name,
  lat: a.lat,
  lon: a.lon,
})).sort((a, b) => a.iata.localeCompare(b.iata));

const BY_IATA = new Map(SEARCH_AIRPORTS.map((a) => [a.iata, a]));
export const airportByIata = (iata) => BY_IATA.get((iata || "").toUpperCase()) || null;

// ── Deterministic PRNG (so a given route+date always yields the same fares) ──
// xmur3 string hash -> mulberry32 generator. No Math.random(), so results are
// stable across reloads and identical on every device.
function hashSeed(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}
function rng(seedStr) {
  let a = hashSeed(seedStr)();
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Carrier pool ─────────────────────────────────────────────────────────────
// A believable spread of carriers; we bias toward ones "based" near the route so
// results feel plausible without needing a real route database.
const CARRIERS = [
  { code: "AA", name: "American", region: "na" },
  { code: "UA", name: "United", region: "na" },
  { code: "DL", name: "Delta", region: "na" },
  { code: "B6", name: "JetBlue", region: "na" },
  { code: "AC", name: "Air Canada", region: "na" },
  { code: "BA", name: "British Airways", region: "eu" },
  { code: "LH", name: "Lufthansa", region: "eu" },
  { code: "AF", name: "Air France", region: "eu" },
  { code: "KL", name: "KLM", region: "eu" },
  { code: "EI", name: "Aer Lingus", region: "eu" },
  { code: "FR", name: "Ryanair", region: "eu" },
  { code: "IB", name: "Iberia", region: "eu" },
  { code: "EK", name: "Emirates", region: "me" },
  { code: "QR", name: "Qatar Airways", region: "me" },
  { code: "SQ", name: "Singapore Airlines", region: "as" },
  { code: "QF", name: "Qantas", region: "oc" },
];

// Very rough region from coordinates — enough to pick plausible carriers.
function regionOf(a) {
  if (!a) return "na";
  const { lat, lon } = a;
  if (lon < -30) return "na";
  if (lon >= -30 && lon < 40 && lat > 25) return "eu";
  if (lon >= 30 && lon < 65) return "me";
  if (lon >= 60 && lat < 40) return "as";
  if (lon >= 110 && lat < 0) return "oc";
  return "eu";
}

function carriersFor(origin, destination, rand) {
  const want = new Set([regionOf(origin), regionOf(destination)]);
  const local = CARRIERS.filter((c) => want.has(c.region));
  const pool = local.length >= 3 ? local : CARRIERS;
  // Stable shuffle via the seeded RNG.
  return [...pool].sort(() => rand() - 0.5);
}

// ── Fare model ───────────────────────────────────────────────────────────────
const DAY_MS = 86_400_000;
export const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const daysBetween = (a, b) => Math.round((startOfDay(a) - startOfDay(b)) / DAY_MS);

// Booking-window curve: last-minute is dear, ~3–8 weeks out is cheapest, very
// far out drifts up a little. Returns a multiplier around 1.0.
function bookingCurve(daysAhead) {
  if (daysAhead <= 0) return 1.9;
  if (daysAhead < 4) return 1.45;
  if (daysAhead < 14) return 1.12;
  if (daysAhead < 21) return 1.0;
  if (daysAhead < 56) return 0.9; // sweet spot
  if (daysAhead < 120) return 1.0;
  return 1.12;
}

// Day-of-week: Tue/Wed cheap, Fri/Sun peak.
const DOW_MULT = [1.05, 0.9, 0.86, 0.9, 1.02, 1.15, 1.0]; // Sun..Sat

// Season by month (0-based): summer + Dec holidays peak.
const MONTH_MULT = [0.92, 0.88, 0.95, 1.0, 1.02, 1.12, 1.2, 1.18, 1.02, 0.98, 0.95, 1.15];

// Estimated economy base fare (USD) for a route on a given date. Deterministic.
export function estimateFare(origin, destination, date, now = new Date()) {
  if (!origin || !destination) return null;
  const km = haversineKm(origin.lat, origin.lon, destination.lat, destination.lon);
  const rand = rng(`${origin.iata}-${destination.iata}-${startOfDay(date).toISOString()}`);
  const base = 42 + km * 0.085; // economy $/km, plus fixed taxes/fees
  const daysAhead = daysBetween(date, now);
  const jitter = 0.88 + rand() * 0.3; // ±~15% stable noise
  const price =
    base *
    bookingCurve(daysAhead) *
    DOW_MULT[startOfDay(date).getDay()] *
    MONTH_MULT[startOfDay(date).getMonth()] *
    jitter;
  return Math.max(29, Math.round(price));
}

// Flight duration (minutes) from distance: cruise ~820 km/h + 35 min taxi/climb.
function durationMin(km) {
  return Math.round((km / 820) * 60 + 35);
}

const pad = (n) => String(n).padStart(2, "0");
const fmtHM = (mins) => `${pad(Math.floor((mins % 1440) / 60))}:${pad(mins % 60)}`;

// ── Public API ───────────────────────────────────────────────────────────────

// The deterministic flight list for one route+day. Shared by the price strip
// and the results view so the two can never disagree.
function buildDayFlights(origin, destination, date, now) {
  const km = haversineKm(origin.lat, origin.lon, destination.lat, destination.lon);
  const dayFare = estimateFare(origin, destination, date, now);
  const rand = rng(`flights-${origin.iata}-${destination.iata}-${startOfDay(date).toISOString()}`);
  const carriers = carriersFor(origin, destination, rand);
  const base = durationMin(km);

  const count = km > 6000 ? 4 + Math.floor(rand() * 3) : 5 + Math.floor(rand() * 4);
  const flights = [];
  for (let i = 0; i < count; i++) {
    const carrier = carriers[i % carriers.length];
    // Spread departures across the day (deterministic), earliest ~05:30.
    const depMin = Math.round(330 + (i * 1080) / count + rand() * 90) % 1440;
    // Long routes sometimes need a connection.
    const stops = km > 9000 && rand() > 0.5 ? 1 : 0;
    const dur = base + stops * (75 + Math.floor(rand() * 90));
    const arrMin = depMin + dur;
    const dayOffset = Math.floor(arrMin / 1440); // arrives next day?
    // Time-of-day fare shaping: early-morning & red-eye cheaper, midday premium.
    const hour = Math.floor(depMin / 60);
    const todMult = hour < 6 ? 0.82 : hour < 9 ? 1.08 : hour < 17 ? 1.0 : hour < 21 ? 1.05 : 0.85;
    const stopMult = stops ? 0.8 : 1.0; // connections are cheaper
    const price = Math.max(29, Math.round(dayFare * todMult * stopMult * (0.9 + rand() * 0.2)));
    flights.push({
      id: `${carrier.code}${100 + Math.floor(rand() * 8800)}`,
      carrier: carrier.name,
      carrierCode: carrier.code,
      depMinutes: depMin,
      arrMinutes: arrMin % 1440,
      arrivesNextDay: dayOffset > 0,
      depTime: fmtHM(depMin),
      arrTime: fmtHM(arrMin),
      durationMin: dur,
      durationLabel: `${Math.floor(dur / 60)}h ${pad(dur % 60)}m`,
      stops,
      price,
    });
  }
  flights.sort((a, b) => a.depMinutes - b.depMinutes);
  return { km: Math.round(km), flights };
}

// A strip of one "from" fare per day, `days` days starting at `startDate` — the
// day's cheapest flight, matching what the results list shows. Powers the
// horizontal price scroller; the cheapest day in range is flagged.
export function priceStrip(originIata, destIata, startDate, days = 60, now = new Date()) {
  const origin = airportByIata(originIata);
  const destination = airportByIata(destIata);
  if (!origin || !destination || origin.iata === destination.iata) return [];
  const out = [];
  let min = Infinity;
  for (let i = 0; i < days; i++) {
    const date = new Date(startOfDay(startDate).getTime() + i * DAY_MS);
    const { flights } = buildDayFlights(origin, destination, date, now);
    const price = flights.reduce((m, f) => Math.min(m, f.price), Infinity);
    if (price < min) min = price;
    out.push({ date, price });
  }
  return out.map((d) => ({ ...d, cheapest: d.price === min }));
}

// The flights for one specific day. Returns a Promise so swapping in a real API
// later is a drop-in change (same return shape).
export async function searchFlights(originIata, destIata, date, now = new Date()) {
  const origin = airportByIata(originIata);
  const destination = airportByIata(destIata);
  if (!origin || !destination || origin.iata === destination.iata) {
    return { origin, destination, date: startOfDay(date), flights: [] };
  }
  const { km, flights } = buildDayFlights(origin, destination, date, now);
  return { origin, destination, date: startOfDay(date), distanceKm: km, flights };
}

// Time-of-day windows for the "time menu" filter.
export const TIME_WINDOWS = [
  { id: "any", label: "Any time", test: () => true },
  { id: "morning", label: "Morning · 05–12", test: (m) => m >= 300 && m < 720 },
  { id: "afternoon", label: "Afternoon · 12–17", test: (m) => m >= 720 && m < 1020 },
  { id: "evening", label: "Evening · 17–21", test: (m) => m >= 1020 && m < 1260 },
  { id: "night", label: "Night · 21–05", test: (m) => m >= 1260 || m < 300 },
];
